import type { ShippingDirection } from '@/contexts/DirectionContext';
import type { ContainerModalData, ContainerModalMovement } from '../interfaces/container.interface';

/**
 * Itinerariul unui container, reconstruit în frontend din mișcările Yang Ming și din ruta
 * booking-ului (backend-ul nu trimite tronsoanele pe import — calea de import nu se modifică).
 *
 * `stops[i]` și `stops[i + 1]` sunt legate de `legs[i]`. Un `OF` deschide un tronson din portul
 * de încărcare, un `DF` îl închide în portul de descărcare. Ce n-a fost încă raportat
 * (feeder-ul spre Constanța, tronsoanele de export rămase) vine din rută, ca planificat.
 */

export type LegState = 'done' | 'active' | 'next' | 'planned';
export type LegRole = 'vessel' | 'mother' | 'feeder';
export type StopKind = 'origin' | 'transshipment' | 'destination';

export interface VoyageStop {
  code: string | null;
  name: string | null;
  kind: StopKind;
  arrival: string | null;
  /** ETA, nu dată reală. */
  arrivalEstimated: boolean;
  departure: string | null;
  departureEstimated: boolean;
  reached: boolean;
  current: boolean;
}

export interface VoyageLeg {
  vesselName: string | null;
  vesselCode: string | null;
  role: LegRole;
  departed: string | null;
  arrived: string | null;
  state: LegState;
  /** Zile pe mare (încheiat) sau de la plecare până azi (în curs). */
  days: number | null;
}

export interface VoyageStep {
  key: string;
  movement: string;
  date: string | null;
  port: string | null;
}

export interface Voyage {
  stops: VoyageStop[];
  legs: VoyageLeg[];
  /** ES / FL la export — înainte de prima navă. */
  before: VoyageStep[];
  /** FC / RE / IE la import — după descărcarea în Constanța. */
  after: VoyageStep[];
  transitDays: number | null;
  transshipments: number;
}

/** Porturile de transbordare frecvente; restul rămân pe cod. */
const HUB_NAMES: Record<string, string> = {
  TRIST: 'Istanbul',
  TRAMR: 'Ambarli',
  TRGEM: 'Gemlik',
  TRMER: 'Mersin',
  TRIZM: 'Izmir',
  GRPIR: 'Piraeus',
  EGPSD: 'Port Said',
  EGALY: 'Alexandria',
  ILHFA: 'Haifa',
  ILASH: 'Ashdod',
  MTMAR: 'Marsaxlokk',
  SGSIN: 'Singapore',
  LKCMB: 'Colombo',
};

const EXPORT_CODES = new Set(['ES', 'FL', 'OF', 'OE', 'DF', 'DE']);
const DAY = 86_400_000;

/** Aceleași mișcări ca în tabul vechi: fără UN; pe import fără ES/FL, pe export doar ciclul de îmbarcare. */
export function visibleMovements(data: ContainerModalData, direction: ShippingDirection): ContainerModalMovement[] {
  const isExport = direction === 'export';
  return data.movements
    .filter((m) => {
      const code = (m.movement || '').toUpperCase();
      if (!code || code === 'UN') return false;
      if (isExport) return EXPORT_CODES.has(code);
      return code !== 'ES' && code !== 'FL';
    })
    .slice()
    .sort((a, b) => time(a.date) - time(b.date));
}

export function portName(data: ContainerModalData, code: string | null | undefined): string | null {
  if (!code) return null;
  const r = data.route;
  const known: Array<[string | null | undefined, string | null | undefined]> = [
    [r.pol, r.polName],
    [r.pod, r.podName],
    [r.tsPort1, r.tsPort1Name],
    [r.tsPort2, r.tsPort2Name],
    [r.etaPort, r.etaPortName],
  ];
  const name = known.find(([c, n]) => c === code && n)?.[1] ?? HUB_NAMES[code] ?? null;
  // Numele din tabelul `port` conțin deja codul („CONSTANTA (ROCND)”); codul îl afișăm separat.
  return name ? name.replace(/\s*\([A-Z0-9]{5}\)\s*$/, '') || name : null;
}

function time(iso: string | null | undefined): number {
  return iso ? new Date(iso).getTime() : 0;
}

function daysBetween(from: string | null, to: string | null): number | null {
  if (!from) return null;
  const end = to ? time(to) : Date.now();
  const d = Math.round((end - time(from)) / DAY);
  return d >= 0 ? d : null;
}

const sameVessel = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.trim().toUpperCase() === b.trim().toUpperCase();

export function buildVoyage(data: ContainerModalData, direction: ShippingDirection): Voyage {
  const isExport = direction === 'export';
  const r = data.route;
  const pod = r.pod;
  const moves = visibleMovements(data, direction);

  const stop = (code: string | null, kind: StopKind): VoyageStop => ({
    code,
    name: portName(data, code),
    kind,
    arrival: null,
    arrivalEstimated: false,
    departure: null,
    departureEstimated: false,
    reached: false,
    current: false,
  });

  const stops: VoyageStop[] = [stop(r.pol, 'origin')];
  const legs: VoyageLeg[] = [];
  const before: VoyageStep[] = [];
  const after: VoyageStep[] = [];
  let open: { leg: VoyageLeg; to: string | null } | null = null;
  const last = () => stops[stops.length - 1];

  moves.forEach((m, i) => {
    const key = `${m.id || i}_${m.movement}`;
    switch (m.movement) {
      case 'ES':
      case 'FL':
        before.push({ key, movement: m.movement, date: m.date, port: m.loadingPort || m.terminal || r.pol });
        return;
      case 'FC':
      case 'RE':
      case 'IE':
        after.push({ key, movement: m.movement, date: m.date, port: m.loadingPort || m.dischargePort || m.terminal });
        return;
      case 'OF':
      case 'OE': {
        const from = m.loadingPort || last().code;
        if (from && last().code && from !== last().code) {
          // Încărcat dintr-un port fără DF raportat (date incomplete) — îl adăugăm ca escală.
          legs.push(emptyLeg('done'));
          stops.push({ ...stop(from, 'transshipment'), reached: true });
        }
        if (!last().code && from) Object.assign(last(), { code: from, name: portName(data, from) });
        Object.assign(last(), { reached: true, departure: m.date, departureEstimated: false });
        const leg: VoyageLeg = {
          vesselName: m.vesselName,
          vesselCode: m.vesselCode,
          role: 'vessel',
          departed: m.date,
          arrived: null,
          state: 'active',
          days: null,
        };
        legs.push(leg);
        open = { leg, to: m.dischargePort };
        return;
      }
      case 'DF':
      case 'DE': {
        const at = m.loadingPort || m.dischargePort;
        if (open) {
          Object.assign(open.leg, { arrived: m.date, state: 'done' as LegState });
          open = null;
        } else if (at && at === last().code) {
          return; // a doua descărcare raportată în același port
        } else {
          legs.push(emptyLeg('done'));
        }
        stops.push({
          ...stop(at, at && at === pod ? 'destination' : 'transshipment'),
          arrival: m.date,
          reached: true,
        });
        return;
      }
      default:
    }
  });

  // Pe navă acum: destinația tronsonului deschis (POD-ul sau următoarea transbordare).
  const openNow = open as { leg: VoyageLeg; to: string | null } | null;
  if (openNow) {
    const to = openNow.to || pod;
    const toPod = !!to && to === pod;
    stops.push(stop(to, toPod ? 'destination' : 'transshipment'));
  }

  // Tronsoanele încă neraportate de Yang Ming vin din rută.
  if (last().code !== pod || !pod) {
    if (isExport) {
      const chain: Array<{ name?: string | null; code?: string | null; to?: string | null }> = [
        { name: r.feederName, code: r.feederCode, to: r.tsPort1 },
        { name: r.motherName, code: r.motherCode, to: r.tsPort2 || pod },
        { name: r.lastFeederName, code: r.lastFeederCode, to: pod },
      ];
      chain
        .filter((c) => c.name && c.to && !legs.some((l) => sameVessel(l.vesselName, c.name)))
        .filter((c) => !stops.some((s) => s.code === c.to))
        .forEach((c) => {
          legs.push({ ...emptyLeg('planned'), vesselName: c.name ?? null, vesselCode: c.code ?? null });
          stops.push(stop(c.to ?? null, c.to === pod ? 'destination' : 'transshipment'));
        });
    } else if (pod && last().code !== pod) {
      // Import: feeder-ul spre Constanța (ruta `rocndVessel`) — „marea neagră" înseamnă manifest
      // importat, nu neapărat navă plecată, deci rămâne „urmează".
      const feeder = !legs.some((l) => sameVessel(l.vesselName, r.vesselName));
      legs.push({
        ...emptyLeg(data.status === 'black_sea' || data.status === 'transshipment' ? 'next' : 'planned'),
        vesselName: feeder ? r.vesselName : null,
        vesselCode: feeder ? r.vesselCode : null,
      });
      stops.push(stop(pod, 'destination'));
    }
    // Ultimul tronson n-are încă navă în rută (ex. export Singapore → Busan).
    if (pod && last().code !== pod) {
      legs.push(emptyLeg('planned'));
      stops.push(stop(pod, 'destination'));
    }
  }

  // ETA-ul din program se referă la `etaPort` (pe export poate fi o transbordare), nu neapărat la POD.
  const etaStop = stops.find((s) => !s.reached && s.code && s.code === (r.etaPort || pod));
  if (etaStop && r.eta && !etaStop.arrival) Object.assign(etaStop, { arrival: r.eta, arrivalEstimated: true });

  const dest = stops[stops.length - 1];
  if (dest.kind !== 'destination' && dest.code && dest.code === pod) dest.kind = 'destination';

  // Statusul spune „descărcat" chiar fără DF raportat: destinația e atinsă.
  const arrivedStatus = ['discharged', 'in_delivery', 'completed'].includes(data.status);
  if (arrivedStatus && dest.kind === 'destination' && !dest.reached) {
    Object.assign(dest, {
      reached: true,
      arrival: data.booking.dischargeDate || dest.arrival,
      arrivalEstimated: !data.booking.dischargeDate,
    });
    legs.forEach((l) => {
      if (l.state !== 'done') l.state = 'done';
    });
  }

  // Plecarea din origine, când OF-ul lipsește.
  if (!stops[0].departure && (r.etd || data.booking.onBoardDate)) {
    stops[0].departure = data.booking.onBoardDate || r.etd;
    stops[0].departureEstimated = !data.booking.onBoardDate;
  }

  legs.forEach((l, i) => {
    l.role = roleFor(l, i, legs.length, isExport, r.motherName);
    if (l.state === 'done' || l.state === 'active') l.days = daysBetween(l.departed, l.arrived);
  });

  // Unde e containerul acum: pe tronsonul activ sau în ultimul port atins.
  if (data.status !== 'completed' && !legs.some((l) => l.state === 'active')) {
    const reached = [...stops].reverse().find((s) => s.reached);
    if (reached) reached.current = true;
  }

  const firstDeparture = stops[0].departureEstimated ? null : stops[0].departure;
  const arrival = dest.reached ? dest.arrival : null;

  return {
    stops,
    legs,
    before,
    after,
    transitDays: daysBetween(firstDeparture, arrival),
    transshipments: stops.filter((s) => s.kind === 'transshipment').length,
  };
}

function emptyLeg(state: LegState): VoyageLeg {
  return { vesselName: null, vesselCode: null, role: 'vessel', departed: null, arrived: null, state, days: null };
}

function roleFor(leg: VoyageLeg, i: number, count: number, isExport: boolean, motherName?: string | null): LegRole {
  if (count === 1) return 'vessel';
  if (isExport) {
    if (sameVessel(leg.vesselName, motherName)) return 'mother';
    return i === 0 || i === count - 1 ? 'feeder' : 'mother';
  }
  return i === count - 1 ? 'feeder' : 'mother';
}
